import { nextFrame } from "../../../library-js/animation/nextFrame";
import { sleep } from "../../../library-js/animation/sleep";
import { DateMath } from "../../../library-js/date/DateMath";
import { ClassFlag } from "../../../library-js/dom-tools/ClassFlag";
import { DOMHitTest } from "../../../library-js/dom-tools/DOMHitTest";
import { ElementAttribute } from "../../../library-js/dom-tools/ElementAttribute";
import { ElementType } from "../../../library-js/dom-tools/ElementType";
import { Events, OnClick, OnDrag, OnSwipe, OnTouchDrag, OnTouchStart } from "../../../library-js/dom-tools/EventListeners";
import { HTMLEditor } from "../../../library-js/dom-tools/HTMLEditor";
import { Link } from "../../../library-js/dom-tools/LinkResolver";
import { DOMOrientation } from "../../../library-js/dom/DOMOrientation";
import { TouchGesture, TouchGestureEvent, TouchGestureEventMoveType } from "../../../library-js/dragging/TouchGesture";
import { HTMLNodeTreeWalker } from "../../../library-js/graphs/HTMLNodeTreeWalker";
import { TreeWalker } from "../../../library-js/graphs/TreeWalker";
import { MathX } from "../../../library-js/math/MathX";
import { RegExpUtility } from "../../../library-js/text/RegExpUtility";
import { Rokojori } from "../../js/Rokojori";
import { CarouselSection } from "./CarouselSection";

export class CarouselSelectElement extends HTMLElement
{
  _carouselSelect:CarouselSelect;
}

export class CarouselSelect
{
  static readonly type = new ElementType( "carousel-select" );

  static readonly mainContainer = new ElementType( "main-container" );

  static readonly ctaStyle    = new ElementType( "cta-style" );
  static readonly ctaElement = new ElementType( "cta-element" );
  static readonly ctaButton = new ElementType( "cta-button" );
  static readonly ctaTarget = new ElementType( "cta-target" );

  static readonly element = new ElementType( "carousel-element" );
  static readonly section = new ElementType( "carousel-section" );

  static readonly item = new ElementType( "carousel-item" );
  static readonly items = new ElementType( "carousel-items" );
  static readonly itemURL = new ElementType( "item-url" );

  static readonly fadeOut = new ClassFlag( "fade-out" );
  static readonly zoomIn = new ClassFlag( "zoom-in" );

  static readonly mainFader = new ElementType( "main-fader" );
  static initialized = false;
  static _elements:CarouselSelect[] = [];

  static applyOnDocument()
  { 
    CarouselSelect.initialize();
    CarouselSelect._elements = [];

    CarouselSelect.type.forAllInDoc( e => CarouselSelect.applyOnElement( e ) );
  }

  static initialize()
  {
    if ( this.initialized )
    {
      return;
    }

    this.initialized = true;  


    Rokojori.$.landscapeScale.onOrientationChange.addListener( 
      ()=>
      {
        CarouselSelect._elements.forEach( c => c.onScreenOrientationChanged() );
      }
    );

    Rokojori.$.onAnimationFrame.addListener(
      ()=>
      {
        CarouselSelect._elements.forEach( c => c.update() )
      }
    );

  }

  

  static applyOnElement( carouselSelect:Element )
  {
    let cse = new CarouselSelect( carouselSelect as HTMLElement );
    
    CarouselSelect._elements.push( cse );
    cse.start();
       
  }


  _element:CarouselSelectElement;
  _mainContainer:HTMLElement;
  _sections:CarouselSection[];
  _items:HTMLElement[];
  _itemsContainer:HTMLElement;
  _activeSectionIndex = -1;
  _fadeDuration:number = 0.3;
  _fader:HTMLElement;
  _offset:number = 0;
  _itemsLeft:string = `calc( var(--sw) * 45 * ( 0.05 ) )`;
  _nextElementIndex = 1;
  _itemsDirection = 0;
  _lastItemsX = 0;
  _itemsDirectionFilterCoefficient = 0.1; 

  _activated = false;

  constructor( element:Element )
  {
    this._element = element as CarouselSelectElement;
    this._element._carouselSelect = this;
  }
  

  start()
  {
    this.getChildren();    
    this.attachListeners();

    
    Rokojori.$.onAnimationFrame.doOnceWhen( 
      ()=> Rokojori.$.loaded,
      async ()=> 
      {
        if (  Rokojori.$.timeElapsedSinceLoaded < 3 )
        {
          await sleep( 500 );
        }

        this._activated = true;

        await Rokojori.$.loadCookieSettings();

        let activeIndex = Rokojori.$.cookieSettings.mainActiveIndex;

        this.setActiveElement( activeIndex );
      }
    );
    
    
    

    
  }

  update()
  {
    if ( ! this._activated )
    {
      return;
    }

    if ( DateMath.isExpiredMS( this._lastInteractionTime, this._showNextDuration * 1000 ) )
    {
      let nextIndex = MathX.nextIndex( this._items, this._activeSectionIndex );
      this.setActiveElement( nextIndex );
    }

    if ( ! DOMOrientation.isPortrait )
    {
      this._itemsDirection = 0;
      return;
    }

    let x = this._itemsContainer.getBoundingClientRect().left;
    let dx = this._lastItemsX - x;
    this._lastItemsX = x;
    this._itemsDirection += this._itemsDirectionFilterCoefficient * ( dx - this._itemsDirection );
  }

  onScreenOrientationChanged()
  {
    if ( DOMOrientation.isLandscape )
    {
      this._itemsContainer.style.left = "0px";
    }    
    
    this.setActiveElement( this._activeSectionIndex );
  }

  getChildren()
  {
    this._mainContainer = CarouselSelect.mainContainer.query( this._element ) as HTMLElement;
    this._sections = CarouselSelect.section.queryAll( this._element ).map( s => new CarouselSection( this, s ) );
    this._items = CarouselSelect.item.queryAll( this._element ) as HTMLElement[];
    this._itemsContainer = CarouselSelect.items.query( this._element ) as HTMLElement;
    this._fader = CarouselSelect.mainFader.query( this._element ) as HTMLElement;
  }

  showBlackOverlay()
  {
    ClassFlag.visible.set( this._fader );
  }

  hideBlackOverlay()
  {
    ClassFlag.visible.removeFrom( this._fader );
  }

  _moved = false;
  _moveTreshold = 10;
  _timeTreholdMS = 400;
  _dragStart = 0;
  _leftOffset = 0;
  _leftOffsetStart = 0;
  _movementSpeed = 1;
  _lastInteractionTime = 0;
  _showNextDuration = 4;
  _startTime = 0;
  _scrollTop = 0;

  attachListeners()
  {
    
    TouchGesture.enable( this._mainContainer, 
      ( tge )=>
      {      
        this.interactionTime = Date.now();

        if ( DOMOrientation.isLandscape )
        {
          return;
        }

        let ctaElement = CarouselSelect.ctaElement.query( this.activeSection._potraitElement.element );

        let targetElement = tge.eventReference.target as Element;
        let walker = HTMLNodeTreeWalker.$;

        if ( targetElement !== ctaElement && walker.childOf( targetElement, ctaElement ) )
        {
          tge.silentEvent = false;
          return;
        }

        if ( TouchGestureEventMoveType.None === tge.moveType || 
             TouchGestureEventMoveType.Vertical === tge.moveType 
          )
        {
          tge.silentEvent = false;

          if ( TouchGestureEventMoveType.Vertical === tge.moveType )
          {
            this._mainContainer.style.transform = `translateX(0px)`;
            return;
          }
        }

        let isStart = Events.isStart( tge.type );
        let isEnd   = Events.isEnd( tge.type );

        if ( isStart )
        {
          this._mainContainer.style.transition = "transform 50ms ease";
        }
        
        let movementX = tge.movementSinceStart.x;
        let max = window.innerWidth / 20;
        let moving = MathX.clamp( movementX, -max, max );
        this._mainContainer.style.transform = `translateX(${-moving}px)`;
        

        if ( ! isEnd )
        {
          return;
        }

        this._mainContainer.style.transition = "transform 500ms ease";
        
        if ( ! tge.xChangedToStart )
        {
          tge.silentEvent = false;
        }
        else if ( movementX > max )
        {
          this.setActiveElement( MathX.repeat( this._activeSectionIndex + 1, this._sections.length ) );      
          
        } 
        else if ( -movementX > max )
        {
          this.setActiveElement( MathX.repeat( this._activeSectionIndex - 1, this._sections.length ) );
           
        }

        this._mainContainer.style.transform = "translateX(0px)";

      }
    );

    
    
    TouchGesture.enable( this._itemsContainer, 
      ( tge )=>
      {

        this.interactionTime = Date.now();

        if ( DOMOrientation.isLandscape )
        {
          return;
        }

        let isStart = /down|start/.test( tge.type );
        let isEnd   = /up|end|cancel/.test( tge.type );
        
       

        let time   = Date.now();
        let mouseX = DOMHitTest.getPointerPosition( tge.eventReference ).x;
        

        if ( isStart )
        {
          this._moved = false;
          this._itemsContainer.style.transition = "none";
          this._dragStart = mouseX;
          this._leftOffsetStart = - this._itemsContainer.offsetLeft;
          this._startTime = Date.now();
          this._scrollTop = window.document.documentElement.scrollTop;
          
        } 

        if ( TouchGestureEventMoveType.Vertical === tge.moveType )
        {
          tge.silentEvent = false;
          this._itemsContainer.style.transition = "left 500ms ease";
          return;
        }

        if ( isEnd )
        {
          let endTime = Date.now();
          let elapsed = endTime - this._startTime;

          if ( ! ( this._moved ) && elapsed < this._timeTreholdMS )
          {
            this._itemsContainer.style.transition = "left 500ms ease";

            let index = this._items.findIndex( it => DOMHitTest.isPointerOver( tge.eventReference, it ) );
            
            if ( index !== -1)
            {
              this.interactionTime = Date.now() + this._waitAfterInteractionMS;
              this.setActiveElement( index );
            }
            
          }
          else
          {
            this._itemsContainer.style.transition = "left 500ms ease-out";

          }
        }

        let movementX = this._dragStart - mouseX;

        movementX = movementX * this._movementSpeed;

        if ( this._moved || Math.abs( movementX ) >= this._moveTreshold )
        {
          this._moved = true;
        }
        else
        {
          return;
        }


        this._leftOffset = - ( Math.round( movementX ) + this._leftOffsetStart );

        if ( isEnd )
        {
          this._leftOffset -= this._itemsDirection * 2;    
          this.interactionTime = Date.now() + this._waitAfterInteractionMS;      
        }

        
        let sw = Rokojori.$.landscapeScale.sw;        

        let min = sw * 45 * ( 0.05 - 3.025 * 0 / 4 );          
        let max = sw * 45 * ( 0.05 - 3.025 * 4 / 4 ); 

        this._leftOffset = MathX.clamp( this._leftOffset, max, min );
        
        let left = `${this._leftOffset}px`;

        this._itemsContainer.style.left = left;

      } 
    )
    
  
    
    this._items.forEach(
      ( item, index )=>
      {
        OnClick.add( item as HTMLElement,
          async ( ce )=>
          {
            if ( DOMOrientation.isPortrait )
            {
              return;
            }

            this.interactionTime = Date.now() + this._waitAfterInteractionMS;
            this.setActiveElement( index );
          }
        )
      }
    )

  }

  get activeSection()
  {
    if ( this._activeSectionIndex === -1 )
    {
      return null;
    }

    return this._sections[ this._activeSectionIndex ];
  }

  _transitionDisabled = false;
  _waitAfterInteractionMS = 1000000;

  set interactionTime( value:number )
  {
    this._lastInteractionTime = Math.max( value, this._lastInteractionTime );
  }

  async setActiveElement( index:number )
  {
    if ( ! this._activated )
    {
      return Promise.resolve();
    }

    this.interactionTime = Date.now();

    if ( this._transitionDisabled )
    {
      this._nextElementIndex = index;
      return Promise.resolve();;
    }
    
    this._transitionDisabled = true;

    this._nextElementIndex = index;
    let item = this._items[ index ];
    ClassFlag.active.setAll( this._items, item );

    let itemsContainer = this._itemsContainer;

    if ( DOMOrientation.isPortrait )
    {
      if ( index === 0 || index === 4 )
      {
        this._itemsLeft = `calc( var(--sw) * 45 * ( 0.05 - 3.025 * ${index} / 4 ) )`;
        
      }
      else
      { 
        this._itemsLeft = `calc( var(--sw) * 45 * ( 0.5 - ${index} ) )`;        
      }

      this._offset = 0;
      itemsContainer.style.left = this._itemsLeft;
    }
    else
    {
      itemsContainer.style.left = "0px";
    }

    let section = this.activeSection;

    if ( section )
    {
      let carouselElement = this.activeSection.getActiveElement();
      let fadeDelay = carouselElement.transitionOutDuration - this._fadeDuration;
      setTimeout( () => this.showBlackOverlay(), fadeDelay * 1000 );

      await this.activeSection.getActiveElement().transitionOut();
      ClassFlag.active.removeFrom( carouselElement.element );
      this.interactionTime = Date.now();
    }
   

    this._activeSectionIndex = this._nextElementIndex;
    Rokojori.$.cookieSettings.mainActiveIndex = this._activeSectionIndex;
    
    Rokojori.$.saveCookieSettings();


    requestAnimationFrame( async ()=> { await nextFrame(); await nextFrame(); this.hideBlackOverlay() } );

    let ce = this.activeSection.getActiveElement();
    ClassFlag.active.set( ce.element );
    await ce.transitionIn();
    this.interactionTime = Date.now();

    this._transitionDisabled = false;

    if ( this._nextElementIndex !== this._activeSectionIndex )
    {
      this.setActiveElement( this._nextElementIndex );
    }
  } 

}